/**
 * CollectionGlowLayer -- blue reach for an illuminated collection
 *
 * When a collection is selected, its members glow blue and pulses travel
 * outward along curves from the densest member toward the distant ones.
 * The gradient runs strong-to-transparent along each curve, so a far-flung
 * member reads as the collection STRETCHING to hold it rather than as a
 * separate blob that happens to be lit.
 *
 * TOPOLOGY: star. One anchor -- the member with the most neighbours nearby
 * -- and a curve from it to every other member. The anchor is the body of
 * the collection; everything else is reach.
 *
 * SOFT EDGES: every glow is filled with a shared radial gradient that
 * falls to zero opacity at its rim, NOT a flat-opacity disc. A flat disc
 * has a hard circular edge that reads as a drawn shape instead of light.
 * One gradient def, reused by every circle; per-circle intensity comes
 * from the `opacity` attribute on top of it.
 *
 * MOTION: a blurred pulse runs along each curve, anchor -> member, on a
 * loop with a per-line stagger. This is what makes the reach read as
 * reaching. Borrowed in spirit from ConnectionLine's flow particles.
 *
 * TWO COLOURS, TWO CLAIMS:
 *   blue (this layer)      = what IS in the collection
 *   fuchsia (lens warmth)  = what RESEMBLES it but isn't in it
 * They are designed to be on screen at the same time. Blue asserts,
 * fuchsia suggests.
 *
 * This layer is decoration only -- pointerEvents is off throughout, so
 * every click passes through to the nodes underneath.
 *
 * Place at: frontend/src/components/map/CollectionGlowLayer.jsx
 */

import React, { useMemo } from 'react';
import { NODE_SIZE } from './mapConstants';

// ---------------------------------------------------------------------------
// TUNING -- everything here is meant to be changed with real data on screen.
// None of it is load-bearing.
// ---------------------------------------------------------------------------

// Stroke width of a reach line. Thinner than a fork line so the glow reads
// as the primary element and the line as the channel it travels along.
// RAISE to 2-3 if the reach feels too timid.
const COLLECTION_LINE_WIDTH = 1.4;

// The collection blue. Kept clear of the fuchsia lens warmth (#d946ef).
// NOTE: this sits close to the digital project-type ring, which is
// blue-purple. If digital members lose their ring inside the glow, shift
// this toward cyan (#0ea5e9) rather than dimming the glow.
const COLLECTION_HUE = '#3b82f6';

// How close two members must be (SVG units, pre-zoom) to count as
// neighbours for cluster amplification.
const CLUSTER_RADIUS = 180;

// Glow multiplier for the most densely surrounded member. 1.0 = no
// amplification; higher makes clusters burn brighter than lone members.
const CLUSTER_AMPLIFY_MAX = 1.6;

// -- Edge softness ----------------------------------------------------------
// Where the radial gradient starts falling off, and how much is left at the
// midpoint. LOWER GLOW_CORE_STOP = softer, more diffuse, less defined.
// RAISE it toward 0.7 for a tighter, brighter core with a thin halo.
const GLOW_CORE_STOP = 0.35;    // solid out to here
const GLOW_MID_STOP = 0.65;     // partial here
const GLOW_MID_ALPHA = 0.35;    // how much is left at the mid stop

// Extra blur on the glow layer. This is what kills any remaining edge.
// 0 disables it. Above ~8 it starts costing frames on big maps.
const GLOW_BLUR = 4;

// -- Motion -----------------------------------------------------------------
// Seconds for one pulse to travel anchor -> member. Longer = more languid,
// more "reaching". Shorter reads as urgent/electric.
const PULSE_DURATION = 3.4;

// Stagger between consecutive lines, so pulses don't fire in lockstep.
const PULSE_STAGGER = 0.45;

// Size and softness of the travelling pulse.
const PULSE_RADIUS = 5;
const PULSE_BLUR = 3;

// Gradient falloff along a reach line. Higher = goes transparent sooner,
// so the reach looks longer and more effortful.
const GLOW_FADE_POWER = 1.8;

// Curvature of a reach line, as a fraction of its length, capped. Close to
// ConnectionLine's own curvature so reach lines feel native to this map,
// but not identical -- they are a different kind of claim.
const CURVE_FACTOR = 0.14;
const CURVE_MAX = 48;

// Base opacities.
const GLOW_OUTER_OPACITY = 0.30;
const GLOW_MID_OPACITY = 0.30;
const RING_OPACITY = 0.5;
const LINE_START_OPACITY = 0.55;
const LINE_MID_OPACITY = 0.20;


/**
 * Quadratic curve between two points, bowed perpendicular to the line.
 */
function curvePath(ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const curvature = Math.min(dist * CURVE_FACTOR, CURVE_MAX);
  const mx = (ax + bx) / 2;
  const my = (ay + by) / 2;
  const cpx = mx + (-dy / dist) * curvature;
  const cpy = my + (dx / dist) * curvature;
  return { d: `M ${ax} ${ay} Q ${cpx} ${cpy} ${bx} ${by}`, dist };
}


const CollectionGlowLayer = ({
  /** All project nodes from CosmicMap's layout pass: { id, x, y, ... } */
  nodes = [],
  /** Project ids belonging to the illuminated collection */
  memberIds = [],
  /** Nothing renders unless this is true */
  active = false,
  /** Unique suffix for def ids, so two layers can't collide */
  idPrefix = 'coll',
}) => {
  const geometry = useMemo(() => {
    if (!active || !memberIds.length || !nodes.length) return null;

    const idSet = new Set(memberIds);
    const members = nodes.filter(n => idSet.has(n.id));
    if (members.length === 0) return null;

    // -- Density: how many other members sit nearby ----------------------
    const withDensity = members.map(m => {
      let neighbors = 0;
      for (const other of members) {
        if (other.id === m.id) continue;
        const dx = other.x - m.x;
        const dy = other.y - m.y;
        if (Math.sqrt(dx * dx + dy * dy) <= CLUSTER_RADIUS) neighbors += 1;
      }
      return { node: m, neighbors };
    });

    const maxNeighbors = withDensity.reduce((mx, m) => Math.max(mx, m.neighbors), 0);

    // -- Anchor: densest member. Ties broken by proximity to the group
    //    centroid, so the anchor sits in the body of the collection rather
    //    than on its edge.
    const cx = members.reduce((s, n) => s + n.x, 0) / members.length;
    const cy = members.reduce((s, n) => s + n.y, 0) / members.length;

    const anchorEntry = withDensity.reduce((best, cand) => {
      if (!best) return cand;
      if (cand.neighbors !== best.neighbors) {
        return cand.neighbors > best.neighbors ? cand : best;
      }
      const dCand = Math.hypot(cand.node.x - cx, cand.node.y - cy);
      const dBest = Math.hypot(best.node.x - cx, best.node.y - cy);
      return dCand < dBest ? cand : best;
    }, null);

    const anchor = anchorEntry.node;

    // -- Amplified glow per member ---------------------------------------
    const glows = withDensity.map(({ node, neighbors }) => {
      const density = maxNeighbors > 0 ? neighbors / maxNeighbors : 0;
      const amp = 1 + density * (CLUSTER_AMPLIFY_MAX - 1);
      return {
        id: node.id,
        x: node.x,
        y: node.y,
        amp,
        isAnchor: node.id === anchor.id,
      };
    });

    // -- Reach lines: star from the anchor outward ------------------------
    const midOffset = Math.max(0.15, Math.min(0.85, 1 / GLOW_FADE_POWER));

    const reaches = members
      .filter(m => m.id !== anchor.id)
      .map((m, i) => {
        const { d, dist } = curvePath(anchor.x, anchor.y, m.x, m.y);
        return {
          key: `${anchor.id}-${m.id}`,
          d,
          dist,
          // Longer reaches take proportionally longer, so a pulse crossing
          // the whole map doesn't move faster than one crossing a cluster.
          dur: PULSE_DURATION * Math.max(0.6, Math.min(1.8, dist / 400)),
          delay: i * PULSE_STAGGER,
          x1: anchor.x, y1: anchor.y,
          x2: m.x, y2: m.y,
        };
      });

    return { glows, reaches, midOffset, anchorId: anchor.id };
  }, [active, memberIds, nodes]);

  if (!geometry) return null;

  const { glows, reaches, midOffset } = geometry;

  const softId = `soft-${idPrefix}`;
  const blurId = `glowblur-${idPrefix}`;
  const pulseBlurId = `pulseblur-${idPrefix}`;

  return (
    <g className="collection-glow-layer" style={{ pointerEvents: 'none' }}>
      <defs>
        {/* Soft radial falloff, shared by every glow. This is what removes
            the hard circular edge -- a flat fill stops dead at its radius
            and reads as a drawn disc rather than as light. */}
        <radialGradient id={softId} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor={COLLECTION_HUE} stopOpacity="1" />
          <stop offset={GLOW_CORE_STOP} stopColor={COLLECTION_HUE} stopOpacity="0.75" />
          <stop offset={GLOW_MID_STOP} stopColor={COLLECTION_HUE} stopOpacity={GLOW_MID_ALPHA} />
          <stop offset="1" stopColor={COLLECTION_HUE} stopOpacity="0" />
        </radialGradient>

        {GLOW_BLUR > 0 && (
          <filter id={blurId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={GLOW_BLUR} />
          </filter>
        )}

        <filter id={pulseBlurId} x="-150%" y="-150%" width="400%" height="400%">
          <feGaussianBlur stdDeviation={PULSE_BLUR} />
        </filter>

        {reaches.map(r => (
          <linearGradient
            key={`grad-${idPrefix}-${r.key}`}
            id={`reach-${idPrefix}-${r.key}`}
            gradientUnits="userSpaceOnUse"
            x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2}
          >
            {/* Strong at the anchor -- the body of the collection */}
            <stop offset="0" stopColor={COLLECTION_HUE} stopOpacity={LINE_START_OPACITY} />
            <stop offset={midOffset} stopColor={COLLECTION_HUE} stopOpacity={LINE_MID_OPACITY} />
            {/* Transparent at the far member -- the reach doesn't quite arrive */}
            <stop offset="1" stopColor={COLLECTION_HUE} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>

      {/* -- Reach lines, drawn beneath the glows -- */}
      {reaches.map(r => (
        <path
          key={`reach-${r.key}`}
          d={r.d}
          fill="none"
          stroke={`url(#reach-${idPrefix}-${r.key})`}
          strokeWidth={COLLECTION_LINE_WIDTH}
          strokeLinecap="round"
        />
      ))}

      {/* -- Travelling pulses: anchor -> member, on a loop --
           This is the motion that makes the reach read as reaching.
           Blurred so it is a moving smudge of light, not a moving dot. */}
      {reaches.map(r => (
        <g key={`pulse-${r.key}`} filter={`url(#${pulseBlurId})`}>
          <circle r={PULSE_RADIUS} fill={COLLECTION_HUE} opacity={0}>
            <animateMotion
              dur={`${r.dur}s`}
              begin={`${r.delay}s`}
              repeatCount="indefinite"
              path={r.d}
              keyPoints="0;1"
              keyTimes="0;1"
              calcMode="spline"
              keySplines="0.4 0 0.6 1"
            />
            {/* Fades in leaving the anchor, out before arriving -- the
                reach never quite lands, same as the line gradient. */}
            <animate
              attributeName="opacity"
              values="0;0.65;0.3;0"
              keyTimes="0;0.25;0.7;1"
              dur={`${r.dur}s`}
              begin={`${r.delay}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="r"
              values={`${PULSE_RADIUS * 0.6};${PULSE_RADIUS};${PULSE_RADIUS * 1.5}`}
              dur={`${r.dur}s`}
              begin={`${r.delay}s`}
              repeatCount="indefinite"
            />
          </circle>
        </g>
      ))}

      {/* -- Per-member glow -- */}
      <g filter={GLOW_BLUR > 0 ? `url(#${blurId})` : undefined}>
        {glows.map(g => {
          const half = NODE_SIZE / 2;
          const outerR = (half + 26) * g.amp;
          const midR = (half + 9) * Math.min(g.amp, 1.25);
          const lo = (GLOW_OUTER_OPACITY * g.amp * 0.6).toFixed(3);
          const hi = (GLOW_OUTER_OPACITY * g.amp * 1.3).toFixed(3);
          return (
            <g key={`glow-${g.id}`} transform={`translate(${g.x}, ${g.y})`}>
              <circle
                r={outerR}
                fill={`url(#${softId})`}
                opacity={GLOW_OUTER_OPACITY * g.amp}
              >
                <animate
                  attributeName="opacity"
                  values={`${lo};${hi};${lo}`}
                  dur="3.2s"
                  repeatCount="indefinite"
                />
              </circle>

              <circle r={midR} fill={`url(#${softId})`} opacity={GLOW_MID_OPACITY} />
            </g>
          );
        })}
      </g>

      {/* -- Rings, OUTSIDE the blur so the membership mark stays crisp --
           The glow is atmosphere; the ring is the statement that this node
           is in the collection. Blurring it would lose that. */}
      {glows.map(g => {
        const ringR = NODE_SIZE / 2 + 3;
        return (
          <g key={`ring-${g.id}`} transform={`translate(${g.x}, ${g.y})`}>
            <circle
              r={ringR}
              fill="none"
              stroke={COLLECTION_HUE}
              strokeWidth={g.isAnchor ? 2.4 : 1.6}
              opacity={RING_OPACITY}
            />

            {/* The anchor breathes, so the collection has a visible centre. */}
            {g.isAnchor && (
              <circle r={ringR + 6} fill="none" stroke={COLLECTION_HUE} strokeWidth={1} opacity={0.3}>
                <animate attributeName="r" values={`${ringR + 3};${ringR + 14};${ringR + 3}`} dur="2.8s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.3;0.03;0.3" dur="2.8s" repeatCount="indefinite" />
              </circle>
            )}
          </g>
        );
      })}
    </g>
  );
};

export default CollectionGlowLayer;